/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/AppointmentRequestSections/Certifications/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React, { useState, useEffect } from "react";
import styles from "./Certifications.module.css";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  getCertificationsOptions,
  getAvailableSectorsOptions,
  getYearsOfExperienceOptions,
  getREPSEProviderOptions,
  getAverageDeliveryTimesOptions,
  getAnnualSalesVolumeOptions,
  getAcceptedPaymentMethodsOptions,
  getCivilLiabilityInsuranceOptions,
  getIMMEXPermitOptions,
  getPracticesImplementedOptions,
} from "../../../data/selectOptions";
import { getSelectedOption, selectStyles } from "../../../utils/common";

const Certifications = ({ company, getFieldValidation }) => {
  const { t } = useTranslation();
  const isTablet = window.innerWidth <= 1023;
  const isMediumMobile = window.innerWidth >= 481 && window.innerWidth <= 767;
  const isSmallMobile = window.innerWidth <= 480;
  const isMobile = isMediumMobile || isSmallMobile;

  const [selectedCertifications, setSelectedCertifications] = useState([]);
  const [isMemberOfBusinessAssociation, setIsMemberOfBusinessAssociation] =
    useState(company?.perteneceAUnaAsociacionEmpresarial === true);
  const [sectors, setSectors] = useState([]);
  const [documentedSystem, setDocumentedSystem] = useState(
    company?.tieneSistemaDocumentado ?? null,
  );

  const certifications = getCertificationsOptions(t);
  const availableSectors = getAvailableSectorsOptions(t);
  const yearsOptions = getYearsOfExperienceOptions(t);
  const REPSEProviderOptions = getREPSEProviderOptions(t);
  const averageDeliveryTimesOptions = getAverageDeliveryTimesOptions(t);
  const annualSalesVolumeOptions = getAnnualSalesVolumeOptions(t);
  const acceptedPaymentMethodsOptions = getAcceptedPaymentMethodsOptions(t);
  const civilLiabilityInsuranceOptions = getCivilLiabilityInsuranceOptions(t);
  const IMMEXPermitOptions = getIMMEXPermitOptions(t);
  const practicesImplementedOptions = getPracticesImplementedOptions(t);

  const [esProveedorREPSE, setEsProveedorREPSE] = useState(
    company?.esProveedorREPSE || "",
  );
  const [tiemposPromedioEntrega, setTiemposPromedioEntrega] = useState(
    company?.tiemposPromedioEntrega || "",
  );
  const [volumenDeVentasAnuales, setVolumenDeVentasAnuales] = useState(
    company?.volumenDeVentasAnuales || "",
  );
  const [metodosDePagoAceptados, setMetodosDePagoAceptados] = useState(
    company?.metodosDePagoAceptados || "",
  );
  const [seguroDeResponsabilidadCivil, setSeguroDeResponsabilidadCivil] =
    useState(company?.seguroDeResponsabilidadCivil || "");
  const [permisoIMMEX, setPermisoIMMEX] = useState(company?.permisoIMMEX || "");
  const [practicasImplementadas, setPracticasImplementadas] = useState(
    company?.practicasImplementadas || "",
  );

  useEffect(() => {
    if (company && company.certificaciones) {
      setSelectedCertifications(company.certificaciones);
    }
    if (company && company.perteneceAUnaAsociacionEmpresarial !== undefined) {
      setIsMemberOfBusinessAssociation(
        company.perteneceAUnaAsociacionEmpresarial === true,
      );
    }
    if (company && company.sectores) {
      const formattedSectors = company.sectores.map((sector) => ({
        value: sector.value,
        experience: sector.experience || null,
      }));
      setSectors(formattedSectors);
    }
  }, [company]);

  const renderCheckboxRows = () => {
    const rows = [];
    for (let i = 0; i < certifications.length; i += 4) {
      const rowItems = certifications.slice(i, i + 4);
      rows.push(
        <div key={`row-${i}`} className={styles.checkboxRow}>
          {rowItems.map((certification) => (
            <div key={certification.value} className={styles.checkboxItem}>
              <input
                type="checkbox"
                id={certification.value}
                className={styles.checkbox}
                checked={selectedCertifications.includes(certification.value)}
                disabled={true}
              />
              <label
                htmlFor={certification.value}
                className={styles.checkboxLabel}
              >
                {t(
                  `components.certifications.certificationOption${
                    certification.value + 1
                  }`,
                )}
              </label>
            </div>
          ))}
        </div>,
      );
    }
    return rows;
  };

  const renderSectorCheckboxRows = () => {
    const rows = [];
    for (let i = 0; i < availableSectors.length; i += 4) {
      const rowItems = availableSectors.slice(i, i + 4);
      rows.push(
        <div key={`sector-row-${i}`} className={styles.checkboxRow}>
          {rowItems.map((sector) => (
            <div key={sector.value} className={styles.checkboxItem}>
              <input
                type="checkbox"
                id={sector.value}
                className={styles.checkbox}
                checked={sectors.some((s) => s.value === sector.value)}
                disabled={true}
              />
              <label htmlFor={sector.value} className={styles.checkboxLabel}>
                {sector.label}
              </label>
            </div>
          ))}
        </div>,
      );
    }
    return rows;
  };

  const renderSectorExperienceRows = () => {
    if (sectors.length === 0) {
      return null;
    }

    const rows = [];
    const sectorsPerRow = isTablet ? 2 : 4;

    for (let i = 0; i < sectors.length; i += sectorsPerRow) {
      const rowSectors = sectors.slice(i, i + sectorsPerRow);

      if (isTablet) {
        // Tablet view: 2 per row with inputRow structure
        const selectRow = (
          <div key={`sector-select-row-${i}`} className={styles.inputRow}>
            {rowSectors.map((sector) => (
              <div key={`select-${sector.value}`} className={styles.inputGroup}>
                <label className={styles.inputLabel}>
                  {t(
                    `components.certifications.sectorOption${sector.value + 1}`,
                  )}
                </label>
                <Select
                  value={getSelectedOption(yearsOptions, sector.experience)}
                  isDisabled={true}
                  options={yearsOptions}
                  placeholder={t(
                    "components.certifications.yearsOfExperienceInTheSectorPlaceholder",
                  )}
                  styles={selectStyles}
                  className="select__control"
                />
              </div>
            ))}
          </div>
        );
        rows.push(selectRow);
      } else {
        // Desktop/Mobile view: 4 per row with checkboxRow structure
        const labelRow = (
          <div key={`sector-label-row-${i}`} className={styles.checkboxRow}>
            {rowSectors.map((sector) => (
              <div
                key={`label-${sector.value}`}
                className={styles.checkboxItem}
              >
                <label
                  className={`${styles.checkboxLabel} ${styles.specialCheckboxLabel}`}
                >
                  {t(
                    `components.certifications.sectorOption${sector.value + 1}`,
                  )}
                </label>
              </div>
            ))}
          </div>
        );

        const selectRow = (
          <div key={`sector-select-row-${i}`} className={styles.checkboxRow}>
            {rowSectors.map((sector) => (
              <div
                key={`select-${sector.value}`}
                className={styles.checkboxItem}
              >
                <Select
                  value={getSelectedOption(yearsOptions, sector.experience)}
                  isDisabled={true}
                  options={yearsOptions}
                  placeholder={t(
                    "components.certifications.yearsOfExperienceInTheSectorPlaceholder",
                  )}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      borderRadius: "8px",
                      fontSize: "14px",
                      cursor: "pointer",
                      borderColor: baseStyles.borderColor,
                      marginBottom: "-10px",
                      width: "300px",
                    }),
                    container: (baseStyles) => ({
                      ...baseStyles,
                    }),
                    valueContainer: (baseStyles) => ({
                      ...baseStyles,
                    }),
                    input: (baseStyles) => ({
                      ...baseStyles,
                    }),
                    option: (baseStyles) => ({
                      ...baseStyles,
                      cursor: "pointer",
                    }),
                    indicatorSeparator: () => ({ display: "none" }),
                  }}
                />
              </div>
            ))}
          </div>
        );

        rows.push(labelRow, selectRow);
      }
    }

    return rows;
  };

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.certifications.title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>
      <div className={styles.checkboxContainer}>{renderCheckboxRows()}</div>
      <div className={styles.sectionHeader}>
        <span className={styles.inputLabel}>
          {t("components.certifications.sectorsAttendedLabel")}
        </span>
      </div>
      <div className={styles.checkboxContainer}>
        {renderSectorCheckboxRows()}
      </div>

      {sectors.length > 0 && (
        <>
          <div className={styles.sectionHeader}>
            <span className={styles.inputLabel}>
              {t("components.certifications.yearsOfExperienceInTheSectorLabel")}
            </span>
          </div>
          <div
            className={`${styles.checkboxContainer} ${styles.sectorsSpacing}`}
          >
            {renderSectorExperienceRows()}
          </div>
        </>
      )}

      <div
        className={`${styles.inputRow} ${
          isTablet ? styles.tabletInputRow : ""
        }`}
      >
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t(
              "components.certifications.hasADocumentedQualityManagementSystemLabel",
            )}
          </label>
          <div className={styles.radioColumn}>
            <div
              className={`${styles.radioOption} ${styles.specialRadioOption}`}
            >
              <input
                type="radio"
                id="hasADocumentedQualityManagementSystemYes1"
                name="hasADocumentedQualityManagementSystem"
                value={0}
                checked={documentedSystem === 0}
                disabled={true}
                className={styles.radio}
              />
              <label
                htmlFor="hasADocumentedQualityManagementSystemYes1"
                className={styles.radioLabel}
              >
                {t(
                  "components.certifications.hasADocumentedQualityManagementSystemYes1",
                )}
              </label>
            </div>
            <div
              className={`${styles.radioOption} ${styles.specialRadioOption}`}
            >
              <input
                type="radio"
                id="hasADocumentedQualityManagementSystemYes2"
                name="hasADocumentedQualityManagementSystem"
                value={1}
                checked={documentedSystem === 1}
                disabled={true}
                className={styles.radio}
              />
              <label
                htmlFor="hasADocumentedQualityManagementSystemYes2"
                className={styles.radioLabel}
              >
                {t(
                  "components.certifications.hasADocumentedQualityManagementSystemYes2",
                )}
              </label>
            </div>
            <div className={styles.radioOption}>
              <input
                type="radio"
                id="hasADocumentedQualityManagementSystemNo"
                name="hasADocumentedQualityManagementSystem"
                value={2}
                checked={documentedSystem === 2}
                disabled={true}
                className={styles.radio}
              />
              <label
                htmlFor="hasADocumentedQualityManagementSystemNo"
                className={styles.radioLabel}
              >
                {t(
                  "components.certifications.hasADocumentedQualityManagementSystemNo",
                )}
              </label>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`${styles.inputRow} ${
          isTablet ? styles.tabletInputRow : ""
        }`}
      >
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.certifications.capacityAndTechnologiesLabel")}
          </label>
          <textarea
            className={`${styles.capacityAndTechnologiesInput} ${
              getFieldValidation && getFieldValidation("capacidadInstalada")
                ? styles.inputError
                : ""
            }`}
            placeholder={t(
              "components.certifications.capacityAndTechnologiesPlaceholder",
            )}
            value={company?.capacidadInstalada || ""}
            disabled={true}
            onChange={() => {}}
            maxLength={300}
          />
        </div>
      </div>
      <div className={styles.inputRow}>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.certifications.REPSEProviderLabel")}
          </label>
          <Select
            value={getSelectedOption(REPSEProviderOptions, esProveedorREPSE)}
            options={REPSEProviderOptions}
            placeholder={t(
              "components.certifications.REPSEProviderPlaceholder",
            )}
            styles={{
              ...selectStyles,
              control: (baseStyles) => ({
                ...selectStyles.control(baseStyles),
                borderColor:
                  getFieldValidation && getFieldValidation("esProveedorREPSE")
                    ? "#dc3545"
                    : baseStyles.borderColor,
              }),
            }}
            className="select__control"
            isDisabled={true}
            onChange={() => {}}
          />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.certifications.averageDeliveryTimesLabel")}
          </label>
          <Select
            value={getSelectedOption(
              averageDeliveryTimesOptions,
              tiemposPromedioEntrega,
            )}
            options={averageDeliveryTimesOptions}
            placeholder={t(
              "components.certifications.averageDeliveryTimesPlaceholder",
            )}
            styles={{
              ...selectStyles,
              control: (baseStyles) => ({
                ...selectStyles.control(baseStyles),
                borderColor:
                  getFieldValidation &&
                  getFieldValidation("tiemposPromedioEntrega")
                    ? "#dc3545"
                    : baseStyles.borderColor,
              }),
            }}
            className="select__control"
            isDisabled={true}
            onChange={() => {}}
          />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.certifications.acceptedPaymentMethodsLabel")}
          </label>
          <Select
            value={getSelectedOption(
              acceptedPaymentMethodsOptions,
              metodosDePagoAceptados,
            )}
            options={acceptedPaymentMethodsOptions}
            placeholder={t(
              "components.certifications.acceptedPaymentMethodsPlaceholder",
            )}
            styles={{
              ...selectStyles,
              control: (baseStyles) => ({
                ...selectStyles.control(baseStyles),
                borderColor:
                  getFieldValidation &&
                  getFieldValidation("metodosDePagoAceptados")
                    ? "#dc3545"
                    : baseStyles.borderColor,
              }),
            }}
            className="select__control"
            isDisabled={true}
            onChange={() => {}}
          />
        </div>
      </div>
      <div className={`${styles.inputRow} ${styles.twoInputsRow}`}>
        <div className={`${styles.inputGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.certifications.civilLiabilityInsuranceLabel")}
          </label>
          <Select
            value={getSelectedOption(
              civilLiabilityInsuranceOptions,
              seguroDeResponsabilidadCivil,
            )}
            options={civilLiabilityInsuranceOptions}
            placeholder={t(
              "components.certifications.civilLiabilityInsurancePlaceholder",
            )}
            styles={{
              ...selectStyles,
              control: (baseStyles) => ({
                ...selectStyles.control(baseStyles),
                borderColor:
                  getFieldValidation &&
                  getFieldValidation("seguroDeResponsabilidadCivil")
                    ? "#dc3545"
                    : baseStyles.borderColor,
              }),
            }}
            className="select__control"
            isDisabled={true}
            onChange={() => {}}
          />
        </div>
        <div className={`${styles.inputGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.certifications.IMMEXPermitLabel")}
          </label>
          <Select
            value={getSelectedOption(IMMEXPermitOptions, permisoIMMEX)}
            options={IMMEXPermitOptions}
            placeholder={t("components.certifications.IMMEXPermitPlaceholder")}
            styles={{
              ...selectStyles,
              control: (baseStyles) => ({
                ...selectStyles.control(baseStyles),
                borderColor:
                  getFieldValidation && getFieldValidation("permisoIMMEX")
                    ? "#dc3545"
                    : baseStyles.borderColor,
              }),
            }}
            className="select__control"
            isDisabled={true}
            onChange={() => {}}
          />
        </div>
        <div className={styles.bottomSpace}></div>
      </div>
    </div>
  );
};

export default Certifications;
